// « Mains vives » (D-163, D-164) : le premier jeu caméra d'Arceau, un drill de réaction « mains » pour le joueur seul, le
// téléphone posé (debout, à hauteur de hanche, à 2–3 m). Ici, ce qui ne dépend pas de l'écran ni de la caméra : les 4 cibles
// aux couleurs des chasubles (D-052), chacune avec sa forme (pour les daltoniens) ; leur place à la taille du joueur, d'après
// ses deux mains en garde ; le moteur d'une partie (la cible annoncée, touchée ou ratée, le temps de réaction, la série) ;
// les niveaux et ce qui les débloque ; « Ma progression » (la semaine, les séances, les records), sans classement.
// La vidéo et l'image ne quittent jamais le téléphone : la base ne garde que la date, la durée, le niveau et les trois
// chiffres (touchées, temps moyen, meilleure série). Logique pure, testée (mains-vives.test.ts).
// D-165 : le niveau 4, « Dribble et réaction » : une main dribble, l'autre touche la cible (du côté de la main libre) ; le
// dribble est repéré par le rythme du poignet qui monte et descend (DribbleTracker), sans suivre le ballon ; une touche ne
// compte que si le dribble continue.
// D-165, demandes d'Idries : la position jambes fléchies exigée (le modèle de pose, à basse fréquence : StanceTracker ; une
// cible touchée debout ne compte pas) ; les cibles espacées, à hauteur d'épaule et de genou, à l'écartement des bras
// tendus d'après la carrure mesurée au calage (Body), plus loin à chaque niveau, sans sortir de l'image.

/** Les 4 places des niveaux 1 à 3 ; ML / MR : à mi-hauteur, bras tendu sur le côté (le niveau 4, du côté de la main libre). */
export type TargetKey = 'HL' | 'HR' | 'BL' | 'BR' | 'ML' | 'MR';
export const TARGET_KEYS: TargetKey[] = ['HL', 'HR', 'BL', 'BR'];
export type ShapeKey = 'rond' | 'carre' | 'tri' | 'losange';
export type Level = 1 | 2 | 3 | 4;
/** La main qui dribble, vue du joueur (l'image est en miroir : sa droite est à droite de l'écran). */
export type Side = 'L' | 'R';

export interface TargetColor { key: 'rouge' | 'bleu' | 'jaune' | 'vert'; hex: string; ink: string; word: string; /** « la cible verte » */ fem: string; shape: ShapeKey; freq: number }
/** Les 4 couleurs, dans l'ordre de leur place de départ (en haut à gauche, en haut à droite, en bas à gauche, en bas à droite). */
export const COLORS: TargetColor[] = [
  { key: 'rouge', hex: '#EF4444', ink: '#FFFFFF', word: 'Rouge', fem: 'rouge', shape: 'rond', freq: 523 },
  { key: 'bleu', hex: '#3B82F6', ink: '#FFFFFF', word: 'Bleu', fem: 'bleue', shape: 'carre', freq: 659 },
  { key: 'jaune', hex: '#FACC15', ink: '#111827', word: 'Jaune', fem: 'jaune', shape: 'tri', freq: 784 },
  { key: 'vert', hex: '#22C55E', ink: '#FFFFFF', word: 'Vert', fem: 'verte', shape: 'losange', freq: 988 },
];
/** La couleur de chaque place ; au niveau 4, les 3 cibles d'un côté ont 3 couleurs différentes. */
export const HOME: Record<TargetKey, number> = { HL: 0, HR: 1, BL: 2, BR: 3, ML: 1, MR: 0 };
export const WHERE: Record<TargetKey, string> = { HL: 'en haut à gauche', HR: 'en haut à droite', BL: 'en bas à gauche', BR: 'en bas à droite', ML: 'à gauche, à mi-hauteur', MR: 'à droite, à mi-hauteur' };
/** Niveau 3 : la flèche (vue de l'écran, l'image est en miroir : à droite de l'écran, c'est la droite du joueur). */
export const ARROW: Record<TargetKey, string> = { HL: '↖', HR: '↗', BL: '↙', BR: '↘', ML: '←', MR: '→' };
export const ARROW_WORD: Record<TargetKey, string> = { HL: 'Haut', HR: 'Haut', BL: 'Bas', BR: 'Bas', ML: 'Gauche', MR: 'Droite' };
/** Les cibles en jeu : les 4 places ; au niveau 4, les 3 du côté de la main libre (en haut, à mi-hauteur, en bas). */
export function keysFor(level: Level, dribble: Side = 'R'): TargetKey[] {
  if (level !== 4) return TARGET_KEYS;
  return dribble === 'R' ? ['HL', 'ML', 'BL'] : ['HR', 'MR', 'BR'];
}
export const SIDE_WORD: Record<Side, string> = { L: 'gauche', R: 'droite' };

export interface LevelInfo { n: Level; title: string; short: string; hint: string }
export const LEVELS: LevelInfo[] = [
  { n: 1, title: 'Places fixes', short: 'places fixes', hint: 'La cible annoncée s’allume : touche-la.' },
  { n: 2, title: 'Les couleurs bougent', short: 'ça bouge', hint: 'Les couleurs changent de place : trouve celle annoncée.' },
  { n: 3, title: 'La flèche', short: 'la flèche', hint: 'Une flèche dit où toucher : lis-la, pas la couleur.' },
  { n: 4, title: 'Dribble et réaction', short: 'dribble', hint: 'Une main dribble, l’autre touche la cible : la touche compte si le dribble continue.' },
];
/** Ce qui débloque un niveau : 3 séances du niveau d'avant avec une moyenne sous ce temps. Le niveau 4 (D-165) : 3 moyennes
 *  sous 0,90 s au niveau 3 (le premier seuil : la flèche se lit plus lentement). */
export type Locked = 2 | 3 | 4;
export const UNLOCK: Record<Locked, { from: Level; count: number; avgMs: number }> = {
  2: { from: 1, count: 3, avgMs: 900 }, 3: { from: 2, count: 3, avgMs: 700 }, 4: { from: 3, count: 3, avgMs: 900 },
};

/** La durée d'une séance (s) : 60 par défaut, 90 au plus (D-164) ; la vidéo ne dépasse jamais 90 s. */
export const DURATIONS = [30, 45, 60, 90] as const;
export const DEFAULT_DURATION = 60;
export const VIDEO_MAX_S = 90;
export const COUNTDOWN_S = 3;

// --- L'image de la caméra et les cibles ------------------------------------------------------------------------------
/** L'image de la caméra avant, en portrait (3:4), en unités de l'écran du jeu (390 × 844 : 188 en haut, 520, 136 en bas). */
export const CAM_W = 390, CAM_H = 520;
export const STAGE_W = 390, STAGE_H = 844, TOP_H = 188, BOT_H = 136;
export const TARGET_R = 31;
/** Une main est dans la cible à moins de ce rayon (un peu plus large que le disque dessiné). */
export const HIT_R = TARGET_R * 1.35;
/** La taille d'une main (poignet → base du majeur), en mètres : l'échelle du joueur. Plus petite chez un enfant, comme lui. */
export const HAND_M = 0.095;
/** Le champ horizontal de la caméra avant en portrait (3:4), à peu près : pour dire « ≈ 2,5 m ». */
const HFOV = (55 * Math.PI) / 180;

export interface Pt { x: number; y: number }
/** Une main repérée : son centre (paume) et sa taille (poignet → base du majeur), dans l'image de la caméra ; son poignet
 *  (le rythme du dribble, D-165). */
export interface HandObs extends Pt { size: number; wrist?: Pt }
export interface Placement {
  targets: Record<TargetKey, Pt>;
  /** Pixels (de l'image) par mètre. */
  scale: number;
  /** Les 4 cibles tiennent dans l'image sans être rapprochées : « Toi en entier ». */
  fits: boolean;
  /** La distance estimée au téléphone (m). */
  distance: number;
}
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
export const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);

/** La carrure mesurée au calage par le modèle de pose (en unités de l'image) : le milieu du corps, la hauteur des épaules,
 *  des hanches et des genoux (en position), la largeur d'épaules. */
export interface Body { cx: number; shoulderY: number; hipY: number; kneeY: number; shoulderW: number }
/** L’écartement des cibles, par niveau : au-delà de l’épaule, la part du bras tendu (épaule → paume). Le niveau 1 au bras
 *  tendu, le 2 et le 3 au-delà (un pas chassé, comme en défense) ; le 4 au bras tendu. */
export const REACH: Record<Level, number> = { 1: 1, 2: 1.1, 3: 1.2, 4: 1 };
/** Sans le modèle de pose : une largeur d'épaules de 40 cm ; le bras (épaule → paume) fait 1,65 fois la largeur d'épaules. */
const SHOULDER_M = 0.4, ARM_PER_SHOULDER = 1.65;
/** Le corps « par défaut », d'après les deux mains en garde devant la poitrine : épaules 12 cm au-dessus, hanches 30 cm
 *  et genoux 75 cm en dessous (en position). */
export function bodyFromHands(cx: number, cy: number, scale: number): Body {
  return { cx, shoulderY: cy - 0.12 * scale, hipY: cy + 0.3 * scale, kneeY: cy + 0.75 * scale, shoulderW: SHOULDER_M * scale };
}
/** Une carrure mesurée vraisemblable pour cette échelle (épaules de 22 à 65 cm, genoux au moins 35 cm sous les épaules). */
const bodyOk = (b: Body, scale: number) => b.shoulderW >= 0.22 * scale && b.shoulderW <= 0.65 * scale && b.kneeY - b.shoulderY >= 0.35 * scale
  && b.hipY > b.shoulderY && b.kneeY > b.hipY;

/** Les cibles à la taille du joueur (D-165) : les hautes à hauteur d'épaule, les basses à hauteur de genou (en position),
 *  à l'écartement des bras tendus (les basses un peu plus près : on descend sur les jambes) ; au niveau 4, celle de
 *  mi-hauteur à hauteur de hanche, plus loin. La carrure vient du modèle de pose au calage (`body`), sinon des deux
 *  mains en garde. L'écartement grandit avec le niveau ; s'il ne tient pas dans l'image, il se resserre (« Recule d'un pas »). */
export function placeTargets(hands: HandObs[], o: { level?: Level; body?: Body | null } = {}): Placement | null {
  if (hands.length < 2) return null;
  const [a, b] = [...hands].sort((p, q) => p.x - q.x) as [HandObs, HandObs];
  const scale = clamp(((a.size + b.size) / 2) / HAND_M, 70, 320);
  const level = o.level ?? 1;
  const body = o.body && bodyOk(o.body, scale) ? o.body : bodyFromHands((a.x + b.x) / 2, (a.y + b.y) / 2, scale);
  const { cx } = body;
  const half = body.shoulderW / 2, arm = body.shoulderW * ARM_PER_SHOULDER, k = REACH[level];
  // Niveau 4 : trois cibles d'un côté, en « < » (l'épaule, la hanche plus loin, le genou plus près) pour qu'elles ne se touchent pas.
  const low = level === 4 ? 0.7 : 0.8;
  const out: Record<TargetKey, number> = { HL: -(half + k * arm), HR: half + k * arm, BL: -(half + low * k * arm), BR: half + low * k * arm,
    ML: -(half + 1.25 * k * arm), MR: half + 1.25 * k * arm };
  const ys: Record<TargetKey, number> = { HL: body.shoulderY, HR: body.shoulderY, BL: body.kneeY, BR: body.kneeY, ML: body.hipY, MR: body.hipY };
  const m = TARGET_R + 6;
  // L'écartement se resserre d'un même facteur des deux côtés s'il sort de l'image (les cibles en jeu à ce niveau).
  const keys: TargetKey[] = level === 4 ? ['HL', 'HR', 'ML', 'MR', 'BL', 'BR'] : TARGET_KEYS;
  const left = Math.max(...keys.filter(key => out[key] < 0).map(key => -out[key])), right = Math.max(...keys.filter(key => out[key] > 0).map(key => out[key]));
  const shrink = Math.min(1, (cx - m) / left, (CAM_W - m - cx) / right);
  const fits = shrink >= 0.9 && keys.every(key => ys[key] >= m && ys[key] <= CAM_H - m);
  const targets = Object.fromEntries((Object.keys(out) as TargetKey[]).map(key => [key, {
    x: Math.round(clamp(cx + out[key] * Math.max(0, shrink), m, CAM_W - m)), y: Math.round(clamp(ys[key], m, CAM_H - m)) }])) as Record<TargetKey, Pt>;
  return { targets, scale, fits, distance: (CAM_W / scale) / (2 * Math.tan(HFOV / 2)) };
}
/** « ≈ 2,5 m » (au demi-mètre). */
export const distanceLabel = (m: number) => `≈ ${String(Math.round(m * 2) / 2).replace('.', ',')} m`;
/** La bonne distance : de 1,5 à 3,5 m. */
export const distanceOk = (m: number) => m >= 1.5 && m <= 3.5;
/** Les deux mains levées au-dessus des cibles du haut : le décompte part. */
export function handsRaised(hands: Pt[], p: Placement): boolean {
  const top = Math.min(p.targets.HL.y, p.targets.HR.y) + 12;
  return hands.length >= 2 && hands.filter(h => h.y < top).length >= 2;
}

// --- Le moteur d'une partie ------------------------------------------------------------------------------------------------
/** Le temps laissé pour toucher la cible annoncée, par niveau (ms). */
export const WINDOW_MS: Record<Level, number> = { 1: 2500, 2: 2200, 3: 2000, 4: 2800 };
/** Niveau 4 : le temps de lancer le dribble avant la première cible, et de changer de main quand on alterne (ms). */
export const DRIBBLE_LEAD_MS = 1500;
export const SWITCH_MS = 1800;
/** Niveau 4 : la main qui dribble (« A » : on alterne toutes les `every` cibles, en commençant par la droite). */
export interface DribblePlan { hand: Side | 'A'; every: number }
export const DEFAULT_DRIBBLE: DribblePlan = { hand: 'R', every: 5 };
/** Le niveau 2 change les couleurs de place toutes les 5 cibles. */
const SHUFFLE_EVERY = 5;

/** Un tirage reproductible (les tests, le mode d'essai). */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface Lit { key: TargetKey; color: number; at: number; until: number; warned?: boolean }
export type GameEvent =
  | { type: 'lit'; key: TargetKey; color: number; at: number }
  | { type: 'hit'; key: TargetKey; color: number; ms: number; at: number }
  | { type: 'miss'; key: TargetKey; color: number; at: number }
  | { type: 'shuffle'; at: number }
  /** Niveau 4 : la main libre est dans la cible, mais le dribble s'est arrêté : la touche ne compte pas (dit une fois par cible). */
  | { type: 'nodribble'; key: TargetKey; at: number }
  /** Les jambes tendues (debout) : la touche ne compte pas, « Fléchis les jambes » (dit une fois par cible). */
  | { type: 'standing'; key: TargetKey; at: number }
  /** Niveau 4, en alternant : on change de main. */
  | { type: 'switch'; side: Side; at: number }
  | { type: 'end'; at: number };
export interface Best { ms: number; key: TargetKey; color: number; at: number }
export interface Summary { level: Level; durationS: number; hits: number; total: number; avgMs: number | null; streak: number; best: Best | null }

export class Game {
  readonly level: Level;
  readonly durationMs: number;
  /** La couleur à chaque place (indice dans COLORS) ; le niveau 2 la change. */
  colorAt: Record<TargetKey, number> = { ...HOME };
  lit: Lit | null = null;
  hits = 0;
  misses = 0;
  streak = 0;
  bestStreak = 0;
  best: Best | null = null;
  over = false;
  private times: number[] = [];
  private nextAt = 0;
  private prev: TargetKey | null = null;
  private count = 0;
  private rand: () => number;
  /** Niveau 4 : la main qui dribble en ce moment, et le plan (une main, ou alterner). */
  side: Side;
  readonly plan: DribblePlan | null;
  private switchedAt = 0;

  constructor(o: { level: Level; durationMs: number; seed?: number; dribble?: DribblePlan }) {
    this.level = o.level;
    this.durationMs = o.durationMs;
    this.rand = rng(o.seed ?? Date.now());
    this.plan = o.level === 4 ? { hand: o.dribble?.hand ?? 'R', every: Math.max(2, Math.min(20, Math.round(o.dribble?.every ?? 5))) } : null;
    this.side = this.plan?.hand === 'L' ? 'L' : 'R';
    if (this.plan) this.nextAt = DRIBBLE_LEAD_MS;
  }
  /** Les cibles en jeu (niveau 4 : celles du côté de la main libre). */
  keys(): TargetKey[] { return keysFor(this.level, this.side); }

  get avgMs(): number | null { return this.times.length ? Math.round(this.times.reduce((s, v) => s + v, 0) / this.times.length) : null; }
  /** Le temps qui reste (ms). */
  left(t: number): number { return Math.max(0, this.durationMs - t); }

  /** Avance la partie au temps t (ms depuis le départ, pauses retirées), avec les mains vues et les cibles placées. Niveau 4 :
   *  `hands` ne porte que la main libre, et `o.dribbling` dit si le dribble continue. `o.stance` : false si le joueur est
   *  debout, jambes tendues (une touche ne compte pas ; le temps continue) ; non dit (pose inconnue) : rien ne bloque. */
  step(t: number, hands: Pt[], targets: Record<TargetKey, Pt>, o: { dribbling?: boolean; stance?: boolean } = {}): GameEvent[] {
    if (this.over) return [];
    const ev: GameEvent[] = [];
    if (t >= this.durationMs) { this.over = true; this.lit = null; ev.push({ type: 'end', at: this.durationMs }); return ev; }
    const lit = this.lit;
    if (lit) {
      const c = targets[lit.key];
      const touching = hands.some(h => dist(h, c) <= HIT_R);
      // Ce qui empêche une touche de compter : le dribble arrêté (niveau 4), les jambes tendues.
      const why = this.plan && !o.dribbling ? 'nodribble' as const : o.stance === false ? 'standing' as const : null;
      if (touching && why && t < lit.until) {
        // La touche ne compte pas : la cible reste allumée jusqu'à la fin de son temps.
        if (!lit.warned) { lit.warned = true; ev.push({ type: why, key: lit.key, at: t }); }
      } else if (touching && !why) {
        const ms = Math.max(120, Math.round(t - lit.at));
        this.hits++; this.streak++; this.bestStreak = Math.max(this.bestStreak, this.streak);
        this.times.push(ms);
        if (!this.best || ms < this.best.ms) this.best = { ms, key: lit.key, color: lit.color, at: Math.round(t) };
        ev.push({ type: 'hit', key: lit.key, color: lit.color, ms, at: t });
        this.lit = null; this.nextAt = t + 350 + this.rand() * 300;
      } else if (t >= lit.until) {
        this.misses++; this.streak = 0;
        ev.push({ type: 'miss', key: lit.key, color: lit.color, at: t });
        this.lit = null; this.nextAt = t + 450 + this.rand() * 300;
      }
    }
    // Niveau 4, en alternant : toutes les `every` cibles, on change de main (le temps de relancer le dribble).
    if (!this.lit && t >= this.nextAt && this.plan?.hand === 'A' && this.count > 0 && this.count % this.plan.every === 0 && this.switchedAt !== this.count) {
      this.switchedAt = this.count;
      this.side = this.side === 'R' ? 'L' : 'R';
      this.prev = null;
      this.nextAt = t + SWITCH_MS;
      ev.push({ type: 'switch', side: this.side, at: t });
      return ev;
    }
    if (!this.lit && t >= this.nextAt) {
      if (this.level === 2 && this.count > 0 && this.count % SHUFFLE_EVERY === 0) { this.shuffle(); ev.push({ type: 'shuffle', at: t }); }
      const key = this.pick(hands, targets);
      const color = this.colorAt[key];
      this.lit = { key, color, at: t, until: t + WINDOW_MS[this.level] };
      this.prev = key; this.count++;
      ev.push({ type: 'lit', key, color, at: t });
    }
    return ev;
  }

  /** La prochaine cible : jamais la même deux fois, ni une où une main est déjà. */
  private pick(hands: Pt[], targets: Record<TargetKey, Pt>): TargetKey {
    const keys = this.keys();
    const free = keys.filter(k => k !== this.prev && !hands.some(h => dist(h, targets[k]) <= HIT_R * 1.2));
    const pool = free.length ? free : keys.filter(k => k !== this.prev);
    return pool[Math.floor(this.rand() * pool.length)] ?? 'HL';
  }

  /** Les couleurs changent toutes de place (aucune ne reste). */
  private shuffle(): void {
    const cur = TARGET_KEYS.map(k => this.colorAt[k]);
    let next = cur;
    for (let i = 0; i < 12 && next.some((c, j) => c === cur[j]); i++) next = [...cur].sort(() => this.rand() - 0.5);
    if (next.some((c, j) => c === cur[j])) next = [cur[1]!, cur[0]!, cur[3]!, cur[2]!];
    TARGET_KEYS.forEach((k, j) => { this.colorAt[k] = next[j]!; });
  }

  summary(): Summary {
    return { level: this.level, durationS: Math.round(this.durationMs / 1000), hits: this.hits, total: this.hits + this.misses, avgMs: this.avgMs, streak: this.bestStreak, best: this.best };
  }
}

/** Ce qu'Arceau garde d'une séance : la durée, le niveau et les trois chiffres, rien d'autre. */
export function keptFigures(s: Summary): { duration_s: number; level: Level; hits: number; avg_ms: number | null; streak: number } {
  return { duration_s: s.durationS, level: s.level, hits: s.hits, avg_ms: s.hits ? s.avgMs : null, streak: Math.min(s.streak, s.hits) };
}

// --- Le niveau 4 : le dribble repéré par le rythme du poignet (D-165) ---------------------------------------------------------
// Sans suivre le ballon : le poignet de la main qui dribble monte et descend. On repère ses points bas et hauts (avec une
// marge, pour ne pas compter le tremblement du repérage), puis on regarde les derniers rebonds : assez nombreux, à une
// fréquence et une amplitude plausibles, réguliers, et le dernier tout récent. Une main immobile, un geste isolé (aller
// toucher une cible), un balancement lent, un tremblement rapide ou un bras qui balaie tout le corps ne sont pas un dribble.
export const DRIBBLE = {
  /** Le rythme d’un dribble : de 1 à 4,5 rebonds par seconde. */
  minHz: 1, maxHz: 4.5,
  /** Le poignet monte et descend de 6 cm à 90 cm (d'un point haut à un point bas). */
  minAmpM: 0.06, maxAmpM: 0.9,
  /** La marge sous laquelle un mouvement n'est que le tremblement du repérage (3 cm). */
  noiseM: 0.03,
  /** Les derniers rebonds regardés : il en faut 3 (deux intervalles) pour dire « dribble ». */
  beats: 3,
  /** Le dernier rebond a moins de 1,3 s : sinon, le dribble s’est arrêté. */
  staleMs: 1300,
  /** Régulier : le plus long intervalle au plus 2,2 fois le plus court. */
  regular: 2.2,
  /** La main perdue plus de 0,6 s (flou, hors de l'image) : on reprend le repérage des points à zéro. */
  gapMs: 600,
} as const;

interface Turn { t: number; y: number; low: boolean }
export interface DribbleState { on: boolean; hz: number | null; ampM: number | null }

/** Le repérage du dribble, d'après la hauteur du poignet de la main qui dribble (y vers le bas, en unités de l'image). */
export class DribbleTracker {
  private turns: Turn[] = [];
  private dir: 0 | 1 | -1 = 0;   // 1 : le poignet descend (on cherche le point bas) ; -1 : il monte
  private cand: { t: number; y: number } | null = null;
  private lo: { t: number; y: number } | null = null;
  private hi: { t: number; y: number } | null = null;
  private lastT = -Infinity;

  /** `scale` : pixels de l'image par mètre (la taille de la main au calage). */
  constructor(private scale: number) {}

  reset(): void { this.turns = []; this.dir = 0; this.cand = this.lo = this.hi = null; this.lastT = -Infinity; }

  /** Une image : le temps (ms) et la hauteur du poignet, ou null si la main n'est pas vue. */
  push(t: number, y: number | null): void {
    if (y == null || !Number.isFinite(y)) return;
    if (t - this.lastT > DRIBBLE.gapMs) { this.dir = 0; this.cand = null; this.lo = this.hi = null; }
    this.lastT = t;
    const s = { t, y };
    const h = DRIBBLE.noiseM * this.scale;
    if (this.dir === 0) {
      if (!this.lo || y < this.lo.y) this.lo = s;
      if (!this.hi || y > this.hi.y) this.hi = s;
      if (this.hi.y - this.lo.y >= h) {
        // Le premier point qui compte : le plus ancien des deux ; on part dans l'autre sens.
        if (this.lo.t <= this.hi.t) { this.addTurn(this.lo, false); this.dir = 1; this.cand = this.hi; }
        else { this.addTurn(this.hi, true); this.dir = -1; this.cand = this.lo; }
        this.lo = this.hi = null;
      }
      return;
    }
    const c = this.cand!;
    if (this.dir === 1) {
      if (y > c.y) this.cand = s;
      else if (c.y - y >= h) { this.addTurn(c, true); this.dir = -1; this.cand = s; }
    } else {
      if (y < c.y) this.cand = s;
      else if (y - c.y >= h) { this.addTurn(c, false); this.dir = 1; this.cand = s; }
    }
  }

  private addTurn(p: { t: number; y: number }, low: boolean): void {
    this.turns.push({ t: p.t, y: p.y, low });
    if (this.turns.length > 12) this.turns.shift();
  }

  /** Le dribble continue-t-il au temps t ? Son rythme (rebonds par seconde) et son amplitude (m). */
  state(t: number): DribbleState {
    const off: DribbleState = { on: false, hz: null, ampM: null };
    const recent = this.turns;
    const beats = recent.filter(x => x.low);
    if (beats.length < DRIBBLE.beats) return off;
    const last = beats.slice(-DRIBBLE.beats);
    if (t - last[last.length - 1]!.t > DRIBBLE.staleMs) return off;
    const gaps = last.slice(1).map((b, i) => b.t - last[i]!.t);
    const minGap = 1000 / DRIBBLE.maxHz, maxGap = 1000 / DRIBBLE.minHz;
    if (gaps.some(g => g < minGap || g > maxGap)) return off;
    if (Math.max(...gaps) > DRIBBLE.regular * Math.min(...gaps)) return off;
    // Les amplitudes : chaque montée et chaque descente entre le premier et le dernier de ces rebonds.
    const span = recent.filter(x => x.t >= last[0]!.t && x.t <= last[last.length - 1]!.t);
    const amps = span.slice(1).map((x, i) => Math.abs(x.y - span[i]!.y) / this.scale);
    if (amps.length < 2 * (DRIBBLE.beats - 1) - 1) return off;   // un point haut entre chaque rebond
    if (amps.some(a => a < DRIBBLE.minAmpM || a > DRIBBLE.maxAmpM)) return off;
    const mean = gaps.reduce((a, g) => a + g, 0) / gaps.length;
    const sorted = [...amps].sort((a, b) => a - b);
    return { on: true, hz: Math.round((1000 / mean) * 10) / 10, ampM: Math.round(sorted[Math.floor(sorted.length / 2)]! * 100) / 100 };
  }
}

// --- La position : jambes fléchies (D-165, demande d'Idries) -------------------------------------------------------------------
// Le modèle de pose (MediaPipe Pose Landmarker « lite », sur le téléphone, quelques fois par seconde : la posture change
// lentement) donne les hanches, les genoux et les chevilles. Fléchi : l'angle hanche-genou-cheville (en 3D, la caméra voit
// le joueur de face) sous 155°, ou les jambes raccourcies d'au moins 10 % par rapport au torse (les hanches plus basses que
// debout, mesuré quand l'angle dit « debout ») ; tendu : l'angle au-dessus de 165° et les jambes à leur longueur debout. Entre
// les deux, l'état ne change pas. Sans pose fiable depuis 1,5 s : inconnu (rien ne bloque).
/** Une image du modèle de pose : les 33 points (x, y en unités de l'image, v la visibilité) ; `world` : en 3D (mètres). */
export interface PoseFrame { lm: { x: number; y: number; v: number }[]; world: { x: number; y: number; z: number }[] | null }
const PI = { ls: 11, rs: 12, lh: 23, rh: 24, lk: 25, rk: 26, la: 27, ra: 28 } as const;
const VIS = 0.5;
const mid = (p: Pt, q: Pt): Pt => ({ x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 });
/** La carrure (épaules, hanches, genoux visibles), sinon null. */
export function bodyOf(f: PoseFrame): Body | null {
  const g = (i: number) => f.lm[i];
  const need = [PI.ls, PI.rs, PI.lh, PI.rh, PI.lk, PI.rk].map(g);
  if (need.some(p => !p || p.v < VIS)) return null;
  const [ls, rs, lh, rh, lk, rk] = need as { x: number; y: number }[];
  return { cx: (mid(ls!, rs!).x + mid(lh!, rh!).x) / 2, shoulderY: mid(ls!, rs!).y, hipY: mid(lh!, rh!).y, kneeY: mid(lk!, rk!).y, shoulderW: dist(ls!, rs!) };
}
export interface StanceSample { kneeDeg: number; legRatio: number | null }
const angle3 = (a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }, c: { x: number; y: number; z: number }) => {
  const u = [a.x - b.x, a.y - b.y, a.z - b.z], v = [c.x - b.x, c.y - b.y, c.z - b.z];
  const n = Math.hypot(...u) * Math.hypot(...v);
  return n ? (Math.acos(clamp((u[0]! * v[0]! + u[1]! * v[1]! + u[2]! * v[2]!) / n, -1, 1)) * 180) / Math.PI : 180;
};
/** Ce que dit une image sur les jambes : l'angle des genoux (moyenne des jambes visibles ; en 3D si possible) et la longueur
 *  des jambes rapportée au torse (cheville → hanche sur hanche → épaule). Null si ni hanche, ni genou, ni cheville visibles. */
export function stanceSample(f: PoseFrame): StanceSample | null {
  const legs = ([[PI.lh, PI.lk, PI.la], [PI.rh, PI.rk, PI.ra]] as const).filter(l => l.every(i => (f.lm[i]?.v ?? 0) >= VIS));
  if (!legs.length) return null;
  const deg = legs.map(([h, k, a]) => f.world && f.world[h] && f.world[k] && f.world[a]
    ? angle3(f.world[h]!, f.world[k]!, f.world[a]!)
    : angle3({ ...f.lm[h]!, z: 0 }, { ...f.lm[k]!, z: 0 }, { ...f.lm[a]!, z: 0 }));
  const sh = [PI.ls, PI.rs].map(i => f.lm[i]).filter(p => p && p.v >= VIS) as { x: number; y: number }[];
  const hipY = legs.reduce((s, [h]) => s + f.lm[h]!.y, 0) / legs.length, ankY = legs.reduce((s, [, , a]) => s + f.lm[a]!.y, 0) / legs.length;
  const shY = sh.length ? sh.reduce((s, p) => s + p.y, 0) / sh.length : null;
  const torso = shY == null ? 0 : hipY - shY;
  return { kneeDeg: deg.reduce((s, d) => s + d, 0) / deg.length, legRatio: torso > 4 ? (ankY - hipY) / torso : null };
}
export const STANCE = { bentDeg: 155, straightDeg: 165, standDeg: 168, ratioBent: 0.9, ratioStraight: 0.94, staleMs: 1500 } as const;
export type StanceState = 'bent' | 'straight' | 'unknown';
/** La position au fil des images de pose (les 3 dernières, médianes), avec une marge entre « fléchi » et « tendu ». */
export class StanceTracker {
  private hist: StanceSample[] = [];
  private ref: number | null = null;
  private cur: 'bent' | 'straight' = 'bent';
  private lastT = -Infinity;
  push(t: number, s: StanceSample | null): void {
    if (!s) return;
    this.lastT = t;
    this.hist.push(s); if (this.hist.length > 3) this.hist.shift();
    const med = (vs: number[]) => { const a = [...vs].sort((x, y) => x - y); return a[Math.floor(a.length / 2)]!; };
    const k = med(this.hist.map(h => h.kneeDeg));
    const rs = this.hist.map(h => h.legRatio).filter((v): v is number => v != null);
    const r = rs.length ? med(rs) : null;
    // La longueur des jambes debout : mesurée quand l'angle dit clairement « debout ».
    if (s.kneeDeg >= STANCE.standDeg && s.legRatio != null) this.ref = Math.max(this.ref ?? 0, s.legRatio);
    const shorter = this.ref != null && r != null && r <= this.ref * STANCE.ratioBent;
    const full = this.ref == null || r == null || r >= this.ref * STANCE.ratioStraight;
    if (k <= STANCE.bentDeg || shorter) this.cur = 'bent';
    else if (k >= STANCE.straightDeg && full) this.cur = 'straight';
  }
  state(t: number): StanceState { return t - this.lastT > STANCE.staleMs ? 'unknown' : this.cur; }
}

/** Sépare la main qui dribble de la main libre. Deux mains : la plus à droite de l'écran est la droite du joueur (l'image
 *  est en miroir). Une seule : celle qui est la plus proche de la dernière place connue de chacune, sinon son côté du corps. */
export class HandSplit {
  private last: { d: Pt | null; f: Pt | null } = { d: null, f: null };
  constructor(private midX: number) {}
  reset(): void { this.last = { d: null, f: null }; }
  push(hands: HandObs[], side: Side): { dribble: HandObs | null; free: HandObs | null } {
    const hs = [...hands].sort((a, b) => a.x - b.x);
    let d: HandObs | null = null, f: HandObs | null = null;
    if (hs.length >= 2) {
      const left = hs[0]!, right = hs[hs.length - 1]!;
      [d, f] = side === 'R' ? [right, left] : [left, right];
    } else if (hs.length === 1) {
      const h = hs[0]!;
      const { d: pd, f: pf } = this.last;
      const isD = pd && pf ? dist(h, pd) <= dist(h, pf) : (h.x >= this.midX) === (side === 'R');
      if (isD) d = h; else f = h;
    }
    if (d) this.last.d = d;
    if (f) this.last.f = f;
    return { dribble: d, free: f };
  }
}

// --- Formats -----------------------------------------------------------------------------------------------------------------
/** « 0,84 » (secondes, deux décimales). */
export const sec = (ms: number | null | undefined): string => (ms == null ? '–' : (ms / 1000).toFixed(2).replace('.', ','));
/** « 0:37 ». */
export const clock = (s: number): string => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.floor(Math.max(0, s) % 60)).padStart(2, '0')}`;
export const levelInfo = (n: number): LevelInfo => LEVELS[Math.min(LEVELS.length, Math.max(1, Math.round(n))) - 1]!;

// --- Ma progression (sans classement) ---------------------------------------------------------------------------------------
export interface SessionRow { played_at: string; duration_s: number; level: number; hits: number; avg_ms: number | null; streak: number }

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const DAY_LETTER = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const WEEKDAY = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

/** La semaine en cours (du lundi au dimanche) : un rond par jour, coché s'il y a eu une séance ; aujourd'hui en pointillé. */
export function weekDays(sessions: SessionRow[], today = new Date()): { letter: string; done: boolean; today: boolean }[] {
  const monday = new Date(today); monday.setHours(12, 0, 0, 0); monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  const days = new Set(sessions.map(s => ymd(new Date(s.played_at))));
  const t = ymd(today);
  return DAY_LETTER.map((letter, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return { letter, done: days.has(ymd(d)), today: ymd(d) === t }; });
}

/** Les dernières séances, de la plus ancienne à la plus récente : la date, les touchées (en barres), le temps moyen. */
export function lastBars(sessions: SessionRow[], n = 8): { date: string; hits: number; avg: string; duration: number }[] {
  return [...sessions].sort((a, b) => a.played_at.localeCompare(b.played_at)).slice(-n)
    .map(s => { const d = new Date(s.played_at); return { date: `${d.getDate()}/${d.getMonth() + 1}`, hits: s.hits, avg: sec(s.avg_ms), duration: s.duration_s }; });
}
/** Depuis la première barre affichée : « +8 · −0,13 s » (touchées, temps moyen), à durée égale. */
export function trend(sessions: SessionRow[], n = 8): { hits: number; avgMs: number | null } | null {
  const list = [...sessions].sort((a, b) => a.played_at.localeCompare(b.played_at)).slice(-n);
  if (list.length < 2) return null;
  const first = list[0]!, last = list[list.length - 1]!;
  return { hits: last.hits - first.hits, avgMs: first.avg_ms != null && last.avg_ms != null ? last.avg_ms - first.avg_ms : null };
}
export const trendLabel = (t: { hits: number; avgMs: number | null }): string =>
  `${t.hits >= 0 ? '+' : '−'}${Math.abs(t.hits)}${t.avgMs != null ? ` · ${t.avgMs <= 0 ? '−' : '+'}${sec(Math.abs(t.avgMs))} s` : ''}`;

/** Les records : la meilleure moyenne, la plus longue série, le plus de touchées. */
export function records(sessions: SessionRow[]): { bestAvg: number | null; bestStreak: number; bestHits: number } {
  const avgs = sessions.map(s => s.avg_ms).filter((v): v is number => v != null);
  return { bestAvg: avgs.length ? Math.min(...avgs) : null, bestStreak: Math.max(0, ...sessions.map(s => s.streak)), bestHits: Math.max(0, ...sessions.map(s => s.hits)) };
}

export interface LevelState { n: Level; title: string; state: 'done' | 'open' | 'locked'; sub: string }
/** Le niveau le plus haut débloqué, et quand. */
export function unlocked(sessions: SessionRow[]): { level: Level; at: Record<Level, string | null> } {
  const sorted = [...sessions].sort((a, b) => a.played_at.localeCompare(b.played_at));
  const at: Record<Level, string | null> = { 1: '', 2: null, 3: null, 4: null };
  for (const n of [2, 3, 4] as const) {
    if (at[UNLOCK[n].from] === null) continue;
    const u = UNLOCK[n];
    const ok = sorted.filter(s => s.level === u.from && s.avg_ms != null && s.avg_ms < u.avgMs);
    if (ok.length >= u.count) at[n] = ok[u.count - 1]!.played_at;
  }
  return { level: at[4] ? 4 : at[3] ? 3 : at[2] ? 2 : 1, at };
}
/** « mardi » cette semaine, sinon « le 3 nov. ». */
export function whenLabel(iso: string, today = new Date()): string {
  const d = new Date(iso);
  const days = Math.round((new Date(ymd(today)).getTime() - new Date(ymd(d)).getTime()) / 86400000);
  if (days <= 0) return 'aujourd’hui';
  if (days === 1) return 'hier';
  if (days < 7) return WEEKDAY[d.getDay()]!;
  return `le ${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
}
export function levelsState(sessions: SessionRow[], today = new Date()): LevelState[] {
  const u = unlocked(sessions);
  return LEVELS.map(l => {
    if (l.n < u.level) return { n: l.n, title: l.title, state: 'done', sub: 'fait' };
    if (l.n === u.level) return { n: l.n, title: l.title, state: 'open', sub: l.n === 1 ? (sessions.length ? 'en cours' : 'pour commencer') : `débloqué ${whenLabel(u.at[l.n]!, today)}` };
    const need = UNLOCK[l.n as Locked];
    return { n: l.n, title: l.title, state: 'locked', sub: `${need.count} moyennes sous ${sec(need.avgMs)} s` };
  });
}

/** Les séances faites pour une proposition du coach : depuis la proposition, jusqu'à la fin de sa date limite. */
export function doneFor(sessions: SessionRow[], createdAt: string, dueOn: string): number {
  const end = new Date(`${dueOn}T23:59:59`).getTime(), start = new Date(createdAt).getTime();
  return sessions.filter(s => { const t = new Date(s.played_at).getTime(); return t >= start && t <= end; }).length;
}
/** « jeu. 5 nov. » ; « jeudi 5 » dans une phrase. */
export const dueShort = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
export const dueLong = (d: string) => { const x = new Date(`${d}T12:00:00`); return `${WEEKDAY[x.getDay()]} ${x.getDate()}`; };
